import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Button, message } from 'antd';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { getWaterAnalysisInfo, updateWaterAnalysis } from '../../../Utils/analysis/waterAnalysis';
import moment from 'moment';
import Header from '../../Shared/Header';
import backIcon from "../../../assets/icons/backIcon.svg";
import CustomDatePicker from '../../Shared/CustomDatePicker';

const EditWaterAnalysis = () => {
    const [form] = Form.useForm();
    const { exploitationId, analysisId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        fetchAnalysisDetails();
    }, [analysisId]);

    const fetchAnalysisDetails = async () => {
        try {
            const details = await getWaterAnalysisInfo(analysisId);
            form.setFieldsValue({
                ...details.data,
                date_realisation: moment(details.date_realisation),
            });
        } catch (error) {
            console.error('Erreur lors de la récupération des détails de l\'analyse:', error);
            message.error('Échec de la récupération des détails de l\'analyse');
        }
    };

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formattedValues = {
                ...values,
                date_realisation: values.date_realisation.toISOString(),
                exploitationId: exploitationId,
            };
            await updateWaterAnalysis(analysisId, formattedValues);
            message.success('Analyse d\'eau mise à jour avec succès');
            navigate(`/admin/exploitation/${exploitationId}/water-analysis`);
        } catch (error) {
            console.error('Erreur lors de la mise à jour de l\'analyse d\'eau:', error);
            message.error('Échec de la mise à jour de l\'analyse d\'eau');
        } finally {
            setLoading(false);
        }
    };

    const waterParameters = [
        { key: 'cation', name: 'Cations', isHeader: true },
        { key: 'sodium', name: 'Sodium', symbol: 'Na⁺' },
        { key: 'potassium', name: 'Potassium', symbol: 'K⁺' },
        { key: 'magnesium', name: 'Magnésium', symbol: 'Mg²⁺' },
        { key: 'calcium', name: 'Calcium', symbol: 'Ca²⁺' },
        { key: 'ammonium', name: 'Ammonium', symbol: 'NH₄⁺' },
        { key: 'anion', name: 'Anions', isHeader: true },
        { key: 'chlorures', name: 'Chlorures', symbol: 'Cl⁻' },
        { key: 'sulfates', name: 'Sulfates', symbol: 'SO₄²⁻' },
        { key: 'carbonates', name: 'Carbonates', symbol: 'CO₃²⁻' },
        { key: 'bicarbonates', name: 'Bicarbonates', symbol: 'HCO₃⁻' },
        { key: 'nitrates', name: 'Nitrates', symbol: 'NO₃⁻' },
        { key: 'dihydrogenophosphates', name: 'Dihydrogénophosphates', symbol: 'H₂PO₄⁻' },
        { key: 'oligoElements', name: 'Oligo-éléments', isHeader: true },
        { key: 'cuivre', name: 'Cuivre', symbol: 'Cu²⁺' },
        { key: 'zinc', name: 'Zinc', symbol: 'Zn²⁺' },
        { key: 'fer', name: 'Fer', symbol: 'Fe²⁺/Fe³⁺' },
        { key: 'manganese', name: 'Manganèse', symbol: 'Mn²⁺' },
        { key: 'bore', name: 'Bore', symbol: 'B' },
    ];


    return (
        <div className="bg-white h-full">
            <Header title="Modifier l'Analyse d'Eau" />
            <div className="h-[calc(100%-100px)] p-6">
                <Link
                    to={`/admin/exploitation/${exploitationId}/analysis/eau`}
                    className="cursor-pointer flex pl-5 gap-2 mt-4"
                >
                    <img src={backIcon} alt="" className="w-5" />
                    <div className="font-semibold text-base text-indigo-600">Retour</div>
                </Link>
                <Form
                    form={form}
                    name="editWaterAnalysis"
                    onFinish={onFinish}
                    layout="vertical"
                    className='bg-white max-w-[900px] p-6 rounded-lg shadow-md'
                >
                    <div className="grid grid-cols-2 gap-x-24 mb-4">
                        <Form.Item name="date_realisation" label="Date de réalisation" rules={[{ required: true }]}>
                            <CustomDatePicker />
                        </Form.Item>
                        <Form.Item name="ec" label="EC (µS/cm)">
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name="laboratoire" label="Laboratoire">
                            <Input />
                        </Form.Item>
                        <Form.Item name="ph" label="pH">
                            <InputNumber step={0.1} />
                        </Form.Item>
                        <Form.Item name="echantillon" label="Échantillon">
                            <Input />
                        </Form.Item>
                    </div>

                    <h2 className="text-xl font-semibold mt-6 mb-4">Paramètres de l'eau</h2>
                    <table className="w-full border-collapse border border-gray-300 mb-4">
                        <thead>
                            <tr>
                                <th className="border border-gray-300 bg-gray-300 px-2 py-2">Paramètre</th>
                                <th className="border border-gray-300 bg-gray-300 px-2 py-2"></th>
                                <th className="border border-gray-300 bg-gray-300 px-2 py-2">mg/L</th>
                                <th className="border border-gray-300 bg-gray-300 px-2 py-2">meq/L</th>
                                <th className="border border-gray-300 bg-gray-300 px-2 py-2">Valeur limite</th>
                            </tr>
                        </thead>
                        <tbody>
                            {waterParameters.map(param => (
                                <tr key={param.key}>
                                    {
                                        param.isHeader ?
                                        <td colSpan="5" className="border border-gray-300 bg-gray-200 px-2 py-2 text-left font-semibold">{param.name}</td>
                                        : (

                                                <>
                                                    <td className="border border-gray-300 px-2 py-2">{param.name}</td>
                                                    <td className="border border-gray-300 px-2 py-2">{param.symbol}</td>
                                                    <td className="border border-gray-300 px-2 py-2">
                                                        <Form.Item name={`${param.key}_mg_l`} noStyle>
                                                            <InputNumber step={0.01} />
                                                        </Form.Item>
                                                    </td>
                                                    <td className="border border-gray-300 px-2 py-2">
                                                        <Form.Item name={`${param.key}_meq_l`} noStyle>
                                                            <InputNumber step={0.01} />
                                                        </Form.Item>
                                                    </td>
                                                    <td className="border border-gray-300 px-2 py-2">
                                                        <Form.Item name={`${param.key}_limval`} noStyle>
                                                            <InputNumber step={0.01} />
                                                        </Form.Item>
                                                    </td>
                                                </>
                                            )}
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <h2 className="text-xl font-semibold mt-6 mb-4">Autres paramètres</h2>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <Form.Item name="ph" label="pH">
                            <InputNumber step={0.1} />
                        </Form.Item>
                        <Form.Item name="ec" label="Conductivité électrique (EC) (µS/cm)">
                            <InputNumber step={1} />
                        </Form.Item>
                    </div>

                    <Form.Item className="mt-4">
                        <Button type="primary" htmlType="submit" loading={loading}>
                            Mettre à jour l'Analyse d'Eau
                        </Button>
                    </Form.Item>
                </Form>
            </div>
        </div>
    );
};

export default EditWaterAnalysis;